import { gql } from '@apollo/client';

const FORM_FIELD_FRAGMENT = gql`
  fragment FormFieldFragment on VOLUNTEER_FormTaskFieldType {
    __typename
    taskFieldId
    name
    description
    ... on VOLUNTEER_FormMultipleChoiceTaskFieldType {
      dynamic
      multipleChoiceOptions {
        multipleChoiceFieldOptionId
        name
        order
        allowText
        trigger {
          items {
            item {
              ... on VOLUNTEER_FormItemFieldType {
                formItemId
              }
              ... on VOLUNTEER_FormItemHeadingType {
                formItemId
              }
              ... on VOLUNTEER_FormItemContentType {
                formItemId
              }
              ... on VOLUNTEER_FormItemMediaType {
                formItemId
              }
              ... on VOLUNTEER_FormItemRepeatableGroupType {
                formItemId
              }
            }
            order
          }
        }
      }
    }
    ... on VOLUNTEER_FormCheckboxTaskFieldType {
      dynamic
      checkboxOptions {
        checkboxFieldOptionId
        name
        order
        allowText
        trigger {
          items {
            item {
              ... on VOLUNTEER_FormItemFieldType {
                formItemId
              }
              ... on VOLUNTEER_FormItemHeadingType {
                formItemId
              }
              ... on VOLUNTEER_FormItemContentType {
                formItemId
              }
              ... on VOLUNTEER_FormItemMediaType {
                formItemId
              }
              ... on VOLUNTEER_FormItemRepeatableGroupType {
                formItemId
              }
            }
            order
          }
        }
      }
    }
    ... on VOLUNTEER_FormDropdownTaskFieldType {
      dynamic
      freeSolo
      dropdownOptions {
        dropdownFieldOptionId
        name
        order
        allowText
        trigger {
          items {
            item {
              ... on VOLUNTEER_FormItemFieldType {
                formItemId
              }
              ... on VOLUNTEER_FormItemHeadingType {
                formItemId
              }
              ... on VOLUNTEER_FormItemContentType {
                formItemId
              }
              ... on VOLUNTEER_FormItemMediaType {
                formItemId
              }
              ... on VOLUNTEER_FormItemRepeatableGroupType {
                formItemId
              }
            }
            order
          }
        }
      }
    }
    ... on VOLUNTEER_FormQuizTaskFieldType {
      dynamic
      quizTaskFieldOptions {
        quizTaskFieldOptionId
        name
        order
        allowText
        trigger {
          items {
            item {
              ... on VOLUNTEER_FormItemFieldType {
                formItemId
              }
              ... on VOLUNTEER_FormItemHeadingType {
                formItemId
              }
              ... on VOLUNTEER_FormItemContentType {
                formItemId
              }
              ... on VOLUNTEER_FormItemMediaType {
                formItemId
              }
              ... on VOLUNTEER_FormItemRepeatableGroupType {
                formItemId
              }
            }
            order
          }
        }
      }
      quizTaskFieldCorrect {
        quizTaskFieldCorrectId
        correctText
        quizTaskFieldOption {
          quizTaskFieldOptionId
        }
      }
    }
    ... on VOLUNTEER_FormMatrixTaskFieldType {
      rows {
        matrixRowId
        name
        order
      }
      columns {
        matrixColumnId
        name
        order
      }
    }
    ... on VOLUNTEER_LinearScaleTaskFieldType {
      minValue
      maxValue
      minLabel
      maxLabel
    }
  }
`;

export const GET_FORMS = gql`
  query GetForms {
    orgLogo
    vm {
      forms {
        formId
        title
        description
        successMessage
        isListed
        allowMultipleSubmissions
        requiresApproval
        dateClosed
        dateCreated
        dateLastUpdated
        order
        fieldMappings
        manager {
          managerId
          profile {
            preferredName
          }
        }
        submissionStats {
          totalSubmissions
        }
        formItems {
          __typename
          ... on VOLUNTEER_FormItemFieldType {
            formItemId
            order
            field {
              taskFieldId
              name
            }
          }
          ... on VOLUNTEER_FormItemRepeatableGroupType {
            formItemId
            order
            title
            label
            groupItems {
              __typename
              ... on VOLUNTEER_FormItemFieldType {
                formItemId
                order
                field {
                  taskFieldId
                  name
                }
              }
            }
          }
        }
        notificationConfig {
          formNotificationConfigId
          notifyAdmins
          notifySpecificManagers
          customEmail
          isEnabled
          managers {
            managerId
            profile {
              preferredName
              lastName
              email
            }
          }
        }
      }
    }
  }
`;

export const GET_FORM = gql`
  query GetForm($formId: String!) {
    orgLogo
    vm {
      form(formId: $formId) {
        formId
        title
        fieldMappings
        adminFields {
          adminFieldId
          name
          type
          order
        }
        description
        successMessage
        redirectUrl
        isListed
        dateClosed
        closedMessage
        allowMultipleSubmissions
        requiresApproval
        order
        dateCreated
        dateLastUpdated
        manager {
          managerId
          profile {
            preferredName
          }
        }
        formItems {
          __typename
          ... on VOLUNTEER_FormItemFieldType {
            formItemId
            order
            optional
            triggerItem {
              formTriggerItemId
              order
              trigger {
                ... on VOLUNTEER_DynamicDropdownFieldFormTriggerType {
                  triggerOption {
                    dropdownFieldOptionId
                  }
                }
                ... on VOLUNTEER_DynamicCheckboxFieldFormTriggerType {
                  triggerOption {
                    checkboxFieldOptionId
                  }
                }
                ... on VOLUNTEER_DynamicMultipleChoiceFieldFormTriggerType {
                  triggerOption {
                    multipleChoiceFieldOptionId
                  }
                }
                ... on VOLUNTEER_DynamicQuizFieldFormTriggerType {
                  triggerOption {
                    quizTaskFieldOptionId
                  }
                }
              }
            }
            field {
              __typename
              taskFieldId
              name
              description
              ... on VOLUNTEER_ShortTextTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_LongTextTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_NumberTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_EmailTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_PhoneTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_DateTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_AddressTaskFieldType {
                taskFieldId
                name
                description
                allowManual
                postcodeOnly
              }
              ... on VOLUNTEER_AttachmentTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_SignatureTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_FormMultipleChoiceTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                multipleChoiceOptions {
                  multipleChoiceFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemRepeatableGroupType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
              }
              ... on VOLUNTEER_FormCheckboxTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                checkboxOptions {
                  checkboxFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemRepeatableGroupType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
              }
              ... on VOLUNTEER_FormDropdownTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                freeSolo
                dropdownOptions {
                  dropdownFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemRepeatableGroupType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
              }
              ... on VOLUNTEER_FormQuizTaskFieldType {
                taskFieldId
                name
                description
                dynamic
                quizTaskFieldOptions {
                  quizTaskFieldOptionId
                  name
                  order
                  allowText
                  trigger {
                    items {
                      item {
                        ... on VOLUNTEER_FormItemFieldType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemHeadingType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemContentType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemMediaType {
                          formItemId
                        }
                        ... on VOLUNTEER_FormItemRepeatableGroupType {
                          formItemId
                        }
                      }
                      order
                    }
                  }
                }
                quizTaskFieldCorrect {
                  quizTaskFieldCorrectId
                  correctText
                  quizTaskFieldOption {
                    quizTaskFieldOptionId
                  }
                }
              }
              ... on VOLUNTEER_FormMatrixTaskFieldType {
                taskFieldId
                name
                description
                rows {
                  matrixRowId
                  name
                  order
                }
                columns {
                  matrixColumnId
                  name
                  order
                }
              }
              ... on VOLUNTEER_RatingTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_TimeTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_UrlTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_YesNoTaskFieldType {
                taskFieldId
                name
                description
              }
              ... on VOLUNTEER_LinearScaleTaskFieldType {
                taskFieldId
                name
                description
                minValue
                maxValue
                minLabel
                maxLabel
              }
              ... on VOLUNTEER_NpsTaskFieldType {
                taskFieldId
                name
                description
              }
            }
          }
          ... on VOLUNTEER_FormItemHeadingType {
            formItemId
            order
            heading
          }
          ... on VOLUNTEER_FormItemContentType {
            formItemId
            order
            content
          }
          ... on VOLUNTEER_FormItemPageBreakType {
            formItemId
            order
            title
          }
          ... on VOLUNTEER_FormItemMediaType {
            formItemId
            order
            media {
              taskMediaId
              name
              description
              ... on VOLUNTEER_YoutubeMediaType {
                videoId
              }
              ... on VOLUNTEER_VimeoMediaType {
                videoId
              }
            }
          }
          ... on VOLUNTEER_FormItemRepeatableGroupType {
            formItemId
            order
            title
            description
            label
            minInstances
            maxInstances
            groupItems {
              __typename
              ... on VOLUNTEER_FormItemFieldType {
                formItemId
                order
                optional
                field {
                  ...FormFieldFragment
                }
              }
              ... on VOLUNTEER_FormItemHeadingType {
                formItemId
                order
                heading
              }
              ... on VOLUNTEER_FormItemContentType {
                formItemId
                order
                content
              }
            }
          }
        }
        submissionStats {
          totalSubmissions
        }
        notificationConfig {
          formNotificationConfigId
          notifyAdmins
          notifySpecificManagers
          customEmail
          isEnabled
          managers {
            managerId
            profile {
              preferredName
              lastName
              email
            }
          }
        }
      }
    }
  }
  ${FORM_FIELD_FRAGMENT}
`;

export const GET_FORM_SUBMISSIONS_LIST = gql`
  query GetFormSubmissionsList(
    $formId: String!
    $offset: Int!
    $limit: Int!
    $sortOrder: String
    $startDate: String
    $endDate: String
  ) {
    vm {
      form(formId: $formId) {
        formId
        title
        formItems {
          __typename
          ... on VOLUNTEER_FormItemFieldType {
            formItemId
            order
            optional
            triggerItem {
              formTriggerItemId
              order
            }
            field {
              ...FormFieldFragment
            }
          }
          ... on VOLUNTEER_FormItemHeadingType {
            formItemId
            order
            heading
            triggerItem {
              formTriggerItemId
              order
            }
          }
          ... on VOLUNTEER_FormItemPageBreakType {
            formItemId
            order
            title
          }
          ... on VOLUNTEER_FormItemRepeatableGroupType {
            formItemId
            order
            title
            label
            minInstances
            maxInstances
            groupItems {
              __typename
              ... on VOLUNTEER_FormItemFieldType {
                formItemId
                order
                optional
                field {
                  ...FormFieldFragment
                }
              }
              ... on VOLUNTEER_FormItemHeadingType {
                formItemId
                order
                heading
              }
            }
          }
        }
        requiresApproval
        adminFields {
          adminFieldId
          name
          type
          order
        }
        formSubmissions(
          offset: $offset
          limit: $limit
          sortOrder: $sortOrder
          startDate: $startDate
          endDate: $endDate
        ) {
          formSubmissionId
          submissionDate
          status
          submissionNumber
          commentCount
          creationCount
          adminFieldValues {
            formSubmissionAdminFieldValueId
            adminFieldId
            name
            type
            value
          }
          formFieldValues {
            formFieldValueId
            taskFieldId
            fieldName
            fieldType
            value
            groupInstanceId
            addressFormatted
            addressStreet
            addressSuburb
            addressState
            addressPostcode
            addressCountry
            placesId
            latitude
            longitude
          }
        }
        submissionStats {
          totalSubmissions(startDate: $startDate, endDate: $endDate)
        }
      }
    }
  }
  ${FORM_FIELD_FRAGMENT}
`;

export const GET_FORM_ANALYTICS = gql`
  query GetFormAnalytics($formId: String!, $startDate: String, $endDate: String) {
    vm {
      form(formId: $formId) {
        formId
        submissionStats {
          totalSubmissions(startDate: $startDate, endDate: $endDate)
          submissionsByDate(startDate: $startDate, endDate: $endDate)
          fieldAnalytics(startDate: $startDate, endDate: $endDate)
        }
      }
    }
  }
`;

export const GET_FORM_RECENT_SUBMISSIONS = gql`
  query GetFormRecentSubmissions($formId: String!) {
    vm {
      form(formId: $formId) {
        formId
        formSubmissions(offset: 0, limit: 5, sortOrder: "DESC") {
          formSubmissionId
          submissionDate
        }
      }
    }
  }
`;

export const GET_FORM_SUBMISSION_ROWS = gql`
  query GetFormSubmissionRows($formId: String!, $sortOrder: String, $startDate: String, $endDate: String) {
    vm {
      form(formId: $formId) {
        formId
        submissionRows(sortOrder: $sortOrder, startDate: $startDate, endDate: $endDate)
      }
    }
  }
`;

export const GET_STARRED_FORM_FIELDS = gql`
  query GetStarredFormFields($profileId: String!) {
    vm {
      profile(profileId: $profileId) {
        userPreference {
          starredFormFields
        }
      }
    }
  }
`;

export const GET_FORM_SUBMISSION = gql`
  query GetFormSubmission($formId: String!, $submissionId: String!) {
    vm {
      form(formId: $formId) {
        formId
        title
        requiresApproval
        adminFields {
          adminFieldId
          name
          type
          order
        }
        formSubmission(submissionId: $submissionId) {
          formSubmissionId
          submissionDate
          status
          submissionNumber
          ipAddress
          userAgent
          referrer
          adminFieldValues {
            formSubmissionAdminFieldValueId
            adminFieldId
            name
            type
            value
          }
          formFieldValues {
            formFieldValueId
            taskFieldId
            fieldName
            fieldType
            value
            groupInstanceId
            addressFormatted
            addressStreet
            addressSuburb
            addressState
            addressPostcode
            addressCountry
            placesId
            latitude
            longitude
          }
          comments {
            commentId
            parentId
            content
            submittedDate
            removedDate
            attachments {
              attachmentId
              fileType
              url
              name
            }
            volunteer {
              volunteerId
              profile {
                preferredName
                lastName
                avatarUrl
              }
            }
            reactions {
              reactionId
              emoji
              volunteer {
                volunteerId
              }
            }
          }
          creations {
            formSubmissionCreationId
            entityType
            entityId
            data
            createdAt
            createdByProfile {
              preferredName
              lastName
            }
          }
        }
        submissionStats {
          totalSubmissions
        }
      }
    }
  }
`;

export const GET_TASKS_FOR_FIELD_MAPPING = gql`
  query GetTasksForFieldMapping {
    vm {
      cakes {
        cakeId
        title
        allTasks {
          taskId
          title
          order
          isHidden
          taskItems {
            ... on VOLUNTEER_TaskItemFieldType {
              taskItemId
              field {
                taskFieldId
                name
                __typename
                ... on VOLUNTEER_DropdownTaskFieldType {
                  dropdownOptions {
                    dropdownFieldOptionId
                    name
                  }
                }
                ... on VOLUNTEER_MultipleChoiceTaskFieldType {
                  multipleChoiceOptions {
                    multipleChoiceFieldOptionId
                    name
                  }
                }
                ... on VOLUNTEER_CheckboxTaskFieldType {
                  checkboxOptions {
                    checkboxFieldOptionId
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
